/**
 * Sidebar account summary — collapsible block at top of sidebar showing
 * configurable account-level metrics. FieldSelector-driven.
 *
 * Pulls from /api/v1/aggregated/risk for the active alias scope.
 * "Long juice/cushion" is a calculated field derived from unrealized PnL.
 */
import { useState } from 'react'
import { ChevronDown, ChevronUp } from 'lucide-react'
import { useAggregatedRisk } from '@/hooks/useAggregated'
import { useAccountStore } from '@/stores/accountStore'
import { FieldSelector, type FieldDefinition } from '@/components/ui/field-selector'
import {
  formatCurrencyWhole, formatCurrencyCompact,
} from '@/lib/utils'
import { formatDateTime } from '@/lib/utils/dates'

interface SummaryField extends FieldDefinition {
  format: 'currency' | 'percent' | 'percent1' | 'ratio' | 'compact' | 'integer' | 'datetime' | 'text'
  pick: (totals: TotalsRow, byAccount: ByAccountRow | null) => number | string | null
}

// Aggregate-view shape. Server totals already skip exclude_from_metrics accounts.
interface TotalsRow {
  net_liquidation: number
  buying_power: number
  margin_used: number
  cash: number
  total_exposure: number
  total_unrealized_pnl: number
  // Extended IB-summary fields (summable across accounts)
  positions_value: number
  available_funds: number
  excess_liquidity: number
  equity_with_loan_value: number
  init_margin: number
  maintenance_margin: number
  full_init_margin_req: number
  full_maint_margin_req: number
  sma: number
  reg_t_margin: number
  reg_t_equity: number
  // Server-derived (sum across accounts is meaningful)
  juice_profit_remaining: number
  projected_balance: number
  // Custom metrics — dollar components summable; ratios re-derived client-side
  notional_exposure: number
  short_assignment_exposure: number
  collateral: number
  call_obligation: number
  // Aggregated as MIN across accounts (set during the reduce, not summed)
  day_trades_remaining?: number | null
}

// Per-account row from /api/v1/aggregated/risk by_account.
interface ByAccountRow {
  alias?: string
  /** True when Settings → Brokers marks this account out of portfolio rollups. */
  exclude_from_metrics?: boolean
  net_liquidation: number
  buying_power: number
  margin_used: number
  cash: number
  total_exposure?: number
  total_unrealized_pnl?: number
  share_of_total_nlv_pct?: number
  positions_count?: number
  // Extended IB-summary fields
  positions_value?: number | null
  ib_positions_value?: number | null
  available_funds?: number | null
  excess_liquidity?: number | null
  equity_with_loan_value?: number | null
  init_margin?: number | null
  maintenance_margin?: number | null
  full_init_margin_req?: number | null
  full_maint_margin_req?: number | null
  sma?: number | null
  reg_t_margin?: number | null
  reg_t_equity?: number | null
  cushion?: number | null
  day_trades_remaining?: number | null
  look_ahead_next_change?: number | null
  segment_title?: string | null
  // Server-derived
  juice_profit_remaining?: number | null
  projected_balance?: number | null
  // Custom metrics (compass/brokers/account_metrics.py — bot-parity formulas)
  notional_exposure?: number | null
  short_assignment_exposure?: number | null
  collateral?: number | null
  call_obligation?: number | null
  leverage?: number | null            // ratio (combo-aware as-if-assigned)
  margin_utilization?: number | null  // ratio (maint margin / NLV)
}

// Helper: prefer the per-account value when an alias is active; otherwise
// use the server totals (already exclude_from_metrics-filtered).
function pickNum(
  t: TotalsRow, a: ByAccountRow | null,
  totalsKey: keyof TotalsRow, accountKey: keyof ByAccountRow,
): number | null {
  if (a) {
    const v = a[accountKey]
    return typeof v === 'number' ? v : null
  }
  const tv = t[totalsKey]
  return typeof tv === 'number' ? tv : null
}

const FIELDS: SummaryField[] = [
  // ── Basic Account Info ───────────────────────────────────────────
  { id: 'nlv', label: 'Account Balance', category: 'basic', format: 'currency',
    visible: true, pick: (t, a) => pickNum(t, a, 'net_liquidation', 'net_liquidation') },
  { id: 'cash', label: 'Cash Balance', category: 'basic', format: 'currency',
    visible: true, pick: (t, a) => pickNum(t, a, 'cash', 'cash') },
  { id: 'positions_value', label: 'Positions Value', category: 'basic', format: 'currency',
    visible: true, pick: (t, a) => pickNum(t, a, 'positions_value', 'positions_value') },
  { id: 'ib_positions_value', label: 'IB Positions Value', category: 'basic', format: 'currency',
    visible: true, pick: (_t, a) =>
      typeof a?.ib_positions_value === 'number' ? a.ib_positions_value : null },

  // ── Available Funds & Buying Power ───────────────────────────────
  { id: 'available_funds', label: 'Available Funds', category: 'funds', format: 'currency',
    visible: true, pick: (t, a) => pickNum(t, a, 'available_funds', 'available_funds') },
  { id: 'buying_power', label: 'Buying Power', category: 'funds', format: 'currency',
    visible: true, pick: (t, a) => pickNum(t, a, 'buying_power', 'buying_power') },
  { id: 'excess_liquidity', label: 'Excess Liquidity', category: 'funds', format: 'currency',
    visible: true, pick: (t, a) => pickNum(t, a, 'excess_liquidity', 'excess_liquidity') },
  { id: 'equity_with_loan_value', label: 'Equity with Loan Value', category: 'funds', format: 'currency',
    visible: false, pick: (t, a) => pickNum(t, a, 'equity_with_loan_value', 'equity_with_loan_value') },

  // ── Margin Requirements ──────────────────────────────────────────
  { id: 'initial_margin', label: 'Initial Margin', category: 'margin', format: 'currency',
    visible: false, pick: (t, a) => pickNum(t, a, 'init_margin', 'init_margin') },
  // Same ratio as Trading header "Init marg/NLV": init_margin ÷ net_liquidation.
  // On by default so it shows without hunting the picker.
  { id: 'init_marg_nlv', label: 'Init marg/NLV', category: 'margin', format: 'percent1',
    visible: true, pick: (t, a) => {
      if (a) {
        const init = typeof a.init_margin === 'number' ? a.init_margin : null
        const nlv = typeof a.net_liquidation === 'number' ? a.net_liquidation : null
        return init != null && nlv != null && nlv > 0 ? (init / nlv) * 100 : null
      }
      return t.net_liquidation > 0 && t.init_margin > 0
        ? (t.init_margin / t.net_liquidation) * 100 : null
    } },
  { id: 'maintenance_margin', label: 'Maintenance Margin', category: 'margin', format: 'currency',
    visible: true, pick: (t, a) => pickNum(t, a, 'maintenance_margin', 'maintenance_margin') },
  { id: 'full_init_margin_req', label: 'Full Initial Margin Req', category: 'margin', format: 'currency',
    visible: false, pick: (t, a) => pickNum(t, a, 'full_init_margin_req', 'full_init_margin_req') },
  { id: 'full_maint_margin_req', label: 'Full Maint Margin Req', category: 'margin', format: 'currency',
    visible: false, pick: (t, a) => pickNum(t, a, 'full_maint_margin_req', 'full_maint_margin_req') },

  // ── Additional Metrics ───────────────────────────────────────────
  { id: 'sma', label: 'SMA', category: 'additional', format: 'currency',
    visible: false, pick: (t, a) => pickNum(t, a, 'sma', 'sma') },
  { id: 'day_trades_remaining', label: 'Day Trades Remaining', category: 'additional', format: 'integer',
    visible: false, pick: (t, a) => {
      if (a) return typeof a.day_trades_remaining === 'number' ? a.day_trades_remaining : null
      return typeof t.day_trades_remaining === 'number' ? t.day_trades_remaining : null
    } },
  { id: 'reg_t_margin', label: 'Reg T Margin', category: 'additional', format: 'currency',
    visible: false, pick: (t, a) => pickNum(t, a, 'reg_t_margin', 'reg_t_margin') },
  { id: 'reg_t_equity', label: 'Reg T Equity', category: 'additional', format: 'currency',
    visible: false, pick: (t, a) => pickNum(t, a, 'reg_t_equity', 'reg_t_equity') },
  { id: 'ib_cushion', label: 'Cushion', category: 'additional', format: 'percent',
    visible: true, pick: (t, a) => {
      // IB cushion arrives as a ratio (0.99 = 99%). All-Accounts view uses
      // the bot's weighted derivation: total excess liquidity / total NLV.
      if (a) return typeof a.cushion === 'number' ? a.cushion * 100 : null
      return t.net_liquidation > 0 && t.excess_liquidity > 0
        ? (t.excess_liquidity / t.net_liquidation) * 100 : null
    } },
  { id: 'look_ahead_next_change', label: 'Look Ahead Next Change', category: 'additional', format: 'datetime',
    visible: false, pick: (_t, a) =>
      typeof a?.look_ahead_next_change === 'number' ? a.look_ahead_next_change : null },
  { id: 'segment_title', label: 'Segment Title', category: 'additional', format: 'text',
    visible: false, pick: (_t, a) => a?.segment_title ?? null },

  // ── Custom (server-side compass/brokers/account_metrics.py — verbatim
  //    ports of the bot's trading.ts; one source of truth) ─────────────
  { id: 'notional_exposure', label: 'Notional Exposure', category: 'custom', format: 'currency',
    visible: true, pick: (t, a) =>
      // LONG-side economic exposure: stock at MV, long options at und×100×qty.
      pickNum(t, a, 'notional_exposure', 'notional_exposure') },
  { id: 'collateral', label: 'Collateral', category: 'custom', format: 'currency',
    visible: true, pick: (t, a) =>
      // Short-put assignment cash: Σ strike × mult × |qty| over short puts.
      pickNum(t, a, 'collateral', 'collateral') },
  { id: 'call_obligation', label: 'Call Obligation', category: 'custom', format: 'currency',
    visible: true, pick: (t, a) =>
      // Short-call delivery notional (gross): Σ strike × mult × |qty|.
      pickNum(t, a, 'call_obligation', 'call_obligation') },
  { id: 'leverage', label: 'Leverage', category: 'custom', format: 'ratio',
    visible: true, pick: (t, a) => {
      // Combo-aware: (long notional + short as-if-assigned) / NLV, with
      // covered calls/puts netted server-side. Aggregate = ratio of sums.
      if (a) return typeof a.leverage === 'number' ? a.leverage : null
      const num = (t.notional_exposure ?? 0) + (t.short_assignment_exposure ?? 0)
      return t.net_liquidation > 0 && num > 0 ? num / t.net_liquidation : null
    } },
  { id: 'margin_utilization', label: 'Margin Utilization', category: 'custom', format: 'percent',
    visible: true, pick: (t, a) => {
      if (a) return typeof a.margin_utilization === 'number' ? a.margin_utilization * 100 : null
      return t.net_liquidation > 0 && t.maintenance_margin > 0
        ? (t.maintenance_margin / t.net_liquidation) * 100 : null
    } },
  { id: 'projected_balance', label: 'Projected Balance', category: 'custom', format: 'currency',
    visible: true, pick: (t, a) =>
      // Account Balance + extrinsic remaining on short options (what the
      // balance becomes if every short decays to zero).
      pickNum(t, a, 'projected_balance', 'projected_balance') },
]

const CATEGORY_LABELS: Record<string, string> = {
  basic: 'Basic Account Info',
  funds: 'Available Funds & Buying Power',
  margin: 'Margin Requirements',
  additional: 'Additional Metrics',
  custom: 'Custom',
}

const STORAGE_KEY = 'compass.sidebar.fields'

function loadFields(): SummaryField[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return FIELDS
    const prefs = JSON.parse(raw) as Array<{ id: string; visible: boolean }>
    return FIELDS.map((f) => {
      const p = prefs.find((x) => x.id === f.id)
      return p ? { ...f, visible: p.visible } : f
    })
  } catch {
    return FIELDS
  }
}

function saveFields(fields: SummaryField[]) {
  const prefs = fields.map(({ id, visible }) => ({ id, visible }))
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs)) } catch { /* ignore */ }
}

function formatValue(format: SummaryField['format'], value: number | string | null): string {
  if (value === null || value === undefined) return '—'
  switch (format) {
    case 'currency': return formatCurrencyWhole(value as number)
    case 'percent':  return `${Math.round(value as number)}%`  // Cushion / Margin Utilization — whole numbers
    case 'percent1': return `${(value as number).toFixed(1)}%`  // Init marg/NLV — one decimal (60.6%)
    case 'ratio':    return (value as number).toFixed(2) + 'x'
    case 'compact':  return formatCurrencyCompact(value as number).replace('$', '')
    case 'integer':  return Math.round(value as number).toString()
    case 'datetime': {
      // IB returns epoch seconds; render via the user's Display prefs.
      const epoch = value as number
      if (!isFinite(epoch) || epoch <= 0) return '—'
      return formatDateTime(new Date(epoch * 1000))
    }
    case 'text':     return String(value) || '—'
  }
}

export function SidebarAccountSummary({ collapsed }: { collapsed: boolean }) {
  const [isOpen, setIsOpen] = useState(true)
  const [fields, setFields] = useState<SummaryField[]>(loadFields)

  const activeAlias = useAccountStore((s) => s.activeAlias)
  const risk = useAggregatedRisk()

  const onChange = (next: FieldDefinition[]) => {
    // Carry over format + pick (FieldSelector only handles {id, label, category, visible})
    const merged: SummaryField[] = next.map((nf) => {
      const orig = FIELDS.find((f) => f.id === nf.id)!
      return { ...orig, visible: nf.visible }
    })
    setFields(merged)
    saveFields(merged)
  }

  if (collapsed) return null

  // Server totals already skip exclude_from_metrics accounts (e.g. paper).
  // by_account still includes every connected alias so single-account scope
  // (selecting Paper in the switcher) can show that account's real numbers.
  const byAccount = (risk.data?.by_account ?? []) as ByAccountRow[]
  const emptyTotals = {
    net_liquidation: 0, buying_power: 0, margin_used: 0, cash: 0,
    total_exposure: 0, total_unrealized_pnl: 0,
    positions_value: 0, available_funds: 0, excess_liquidity: 0,
    equity_with_loan_value: 0, init_margin: 0, maintenance_margin: 0,
    full_init_margin_req: 0, full_maint_margin_req: 0,
    sma: 0, reg_t_margin: 0, reg_t_equity: 0,
    juice_profit_remaining: 0, projected_balance: 0,
    notional_exposure: 0, short_assignment_exposure: 0,
    collateral: 0, call_obligation: 0,
  } as TotalsRow
  const totals = (risk.data?.totals ?? emptyTotals) as TotalsRow
  const accountRow = activeAlias
    ? byAccount.find((a) => a.alias === activeAlias) ?? null
    : null
  const headerLabel = activeAlias
    ? activeAlias.toUpperCase()
    : 'ALL ACCOUNTS'

  const visibleFields = fields.filter((f) => f.visible)

  // No data → render "No data." rather than fabricated $0 rows. This is true
  // when the risk query errored or returned no by-account rows (the `totals`
  // object is always a non-null zero-filled aggregate, so the naive `!totals`
  // check never fires — hence the explicit condition here).
  const noData = risk.isError || byAccount.length === 0

  return (
    <div className="border-b border-border/60 bg-muted/20">
      <div className="flex items-center justify-between px-3 py-2">
        <button
          onClick={() => setIsOpen((v) => !v)}
          className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-muted-foreground hover:text-foreground"
        >
          {isOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronUp className="h-3 w-3" />}
          {headerLabel}
        </button>
        <FieldSelector
          fields={fields}
          onFieldsChange={onChange}
          defaultVisible={['nlv', 'cash', 'positions_value', 'ib_positions_value',
            'available_funds', 'buying_power', 'excess_liquidity',
            'maintenance_margin', 'ib_cushion',
            'notional_exposure', 'collateral', 'call_obligation',
            'leverage', 'margin_utilization', 'projected_balance']}
          categoryLabels={CATEGORY_LABELS}
          title="Configure Account Info"
          description="Choose which account fields to display in the sidebar."
        />
      </div>
      {isOpen && (
        <div className="px-3 pb-2 space-y-1">
          {risk.isLoading ? (
            <div className="text-[10px] text-muted-foreground/60 py-1">Loading…</div>
          ) : noData ? (
            <div className="text-[10px] text-muted-foreground/60 py-1">No data.</div>
          ) : visibleFields.length === 0 ? (
            <div className="text-[10px] text-muted-foreground/60 py-1">No fields visible. Click ⚙ to configure.</div>
          ) : (
            visibleFields.map((f) => (
              <div key={f.id} className="flex items-center justify-between text-[11px]">
                <span className="text-muted-foreground">{f.label}</span>
                <span className={`font-mono tabular-nums ${f.id === 'ib_cushion' ? 'text-info' : ''}`}>
                  {formatValue(f.format, f.pick(totals, accountRow || null))}
                </span>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  )
}
